import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSTooltip from "views/system-design/DSTooltip";
import DSChip from "views/system-design/DSChip";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSErrorButton from "views/system-design/DSErrorButton";
import DSPopover from "views/system-design/DSPopover";
import DSMenuItem from "views/system-design/DSMenuItem";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import ReplayIcon from "@mui/icons-material/Replay";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SnoozeIcon from "@mui/icons-material/Snooze";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import GroupIcon from "@mui/icons-material/Group";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionIcon from "@mui/icons-material/Description";
import { Author } from "api/author";
import firebaseService from "firebase_service/firebaseService";
import { formatRelativeDate } from "../../utils";
import SectionHeader from "../../components/shared/SectionHeader";
import DetailDrawer from "../../components/shared/DetailDrawer";
import { extractApiErrorMessage } from "utils/util";
import { useReviewById, useReviewMutations } from "../hooks/useReviewQueries";
import {
  OUTCOME_LABEL,
  POLICY_LABEL,
  PULL_STATE_LABEL,
  REPO_LABEL,
  REVIEWER_STATUS_COLOR,
  REVIEWER_STATUS_LABEL,
  ReviewEvent,
  ReviewRequest,
  SNOOZE_OPTIONS_HOURS,
} from "../types";
import {
  hasNewCommits,
  isRequester,
  isReviewer,
  isTeamRequest,
  slackThreadUrl,
  userName,
} from "../utils";
import { PriorityChip, PullChip, ReviewStatusChip } from "./ReviewChips";
import ReviewRequestModal from "./ReviewRequestModal";

interface ReviewDetailPanelProps {
  // The selected request id (from the URL). `fallback` is the row from the
  // list, shown until the detail fetch lands.
  requestId: string | null;
  fallback: ReviewRequest | null;
  users: Author[];
  onClose: () => void;
}

const describeEvent = (event: ReviewEvent, users: Author[]): string => {
  const actor = event.actorUserId
    ? userName(event.actorUserId, users)
    : (event.actorLogin ?? "GitHub");
  const pull =
    event.repo && event.pullNumber
      ? ` on ${REPO_LABEL[event.repo]} #${event.pullNumber}`
      : "";
  switch (event.type) {
    case "created":
      return `${actor} asked for review`;
    case "re_requested":
      return `${actor} asked again (${event.detail})`;
    case "reviewer_added":
      return `${actor} added ${userName(event.detail, users)} as reviewer`;
    case "reviewer_removed":
      return `${actor} removed ${userName(event.detail, users)} from reviewers`;
    case "review_submitted":
      return `${actor} ${
        event.detail === "approved" ? "approved" : "requested changes"
      }${pull}`;
    case "review_dismissed":
      return `${actor}'s review was dismissed${pull}`;
    case "unmapped_reviewer":
      return `GitHub user "${event.actorLogin}" is not linked to a team member`;
    case "pull_merged":
      return `PR merged${pull}`;
    case "pull_closed":
      return `PR closed without merge${pull}`;
    case "pull_reopened":
      return `PR reopened${pull}`;
    case "new_commits":
      return `New commits pushed${pull} (${event.detail})`;
    case "reminded":
      if (event.detail === "channel") {
        return actor === "GitHub"
          ? "Reminder posted in the Slack thread"
          : `${actor} nudged the team in the Slack thread`;
      }
      return actor === "GitHub"
        ? "Reminder sent to pending reviewers"
        : `${actor} nudged the pending reviewers`;
    case "escalated":
      return "Requester told: nobody reviewed after several reminders";
    case "snoozed":
      return `${actor} snoozed reminders for ${event.detail}`;
    case "cancelled":
      return `${actor} cancelled the request`;
    case "manually_checked":
      return event.detail === event.actorUserId
        ? `${actor} marked it as checked by hand`
        : `${actor} marked ${userName(event.detail, users)} as checked by hand`;
    case "manual_check_removed":
      return event.detail === event.actorUserId
        ? `${actor} removed their manual check`
        : `${actor} removed the manual check of ${userName(event.detail, users)}`;
    case "synced":
      return "State refreshed from GitHub";
    default:
      return event.type;
  }
};

function DetailContent({
  request,
  users,
  onClose,
}: {
  request: ReviewRequest;
  users: Author[];
  onClose: () => void;
}) {
  const uid = firebaseService.getUserId() ?? "";
  const { reRequest, remind, snooze, cancel, markChecked, unmarkChecked } =
    useReviewMutations();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);

  const requester = isRequester(request, uid);
  const reviewer = isReviewer(request, uid);
  const closed = request.status === "closed";
  const checks = request.manualChecks ?? [];
  const isChecked = (userId: string) =>
    checks.some((check) => check.userId === userId);
  const canCheckSelf = !closed && (reviewer || isTeamRequest(request));

  const run = async (name: string, action: () => Promise<unknown>) => {
    setBusy(name);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(extractApiErrorMessage(actionError, "Something went wrong"));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?tab=reviews&review=${request.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const events = [...request.events].reverse();

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 2.5,
          pt: 2,
          pb: 1,
        }}
      >
        <DSBox sx={{ flex: 1, minWidth: 0 }}>
          <DSBox
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
          >
            <ReviewStatusChip request={request} />
            <PriorityChip request={request} />
            {request.ticketKey && (
              <DSTypography
                variant="caption"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                {request.ticketKey}
              </DSTypography>
            )}
            <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
              round {request.round}
            </DSTypography>
          </DSBox>
          <DSTypography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {request.title}
          </DSTypography>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            {userName(request.requesterId, users)} ·{" "}
            {formatRelativeDate(request.createdAt)} ·{" "}
            {isTeamRequest(request)
              ? "open to the team"
              : `${POLICY_LABEL[request.policy].toLowerCase()} must approve`}
          </DSTypography>
        </DSBox>
        <DSTooltip title={copied ? "Copied" : "Copy link"}>
          <DSIconButton size="small" onClick={copyLink}>
            {copied ? (
              <CheckIcon fontSize="small" />
            ) : (
              <LinkIcon fontSize="small" />
            )}
          </DSIconButton>
        </DSTooltip>
        <DSIconButton size="small" onClick={onClose}>
          <CloseIcon />
        </DSIconButton>
      </DSBox>

      <DSBox sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2 }}>
        {request.description && (
          <DSBox sx={{ mb: 3 }}>
            <SectionHeader
              icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
              title="Notes for the reviewer"
            />
            <DSTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {request.description}
            </DSTypography>
          </DSBox>
        )}

        <DSBox sx={{ mb: 3 }}>
          <SectionHeader
            icon={<LinkIcon sx={{ fontSize: 16 }} />}
            title="Pull requests"
          />
          {request.prs.map((pull) => (
            <DSBox
              key={`${pull.githubRepo}#${pull.number}`}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
            >
              <PullChip pull={pull} />
              <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
                {PULL_STATE_LABEL[pull.state]}
                {pull.reviews.length > 0 &&
                  ` · ${pull.reviews
                    .map(
                      (review) =>
                        `${
                          review.userId
                            ? userName(review.userId, users)
                            : review.githubLogin
                        } ${review.state === "approved" ? "✓" : "✗"}`,
                    )
                    .join(", ")}`}
              </DSTypography>
              {pull.hasNewCommitsSinceRound && (
                <DSChip
                  label="new commits"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 10, borderColor: "warning.main" }}
                />
              )}
            </DSBox>
          ))}
        </DSBox>

        <DSBox sx={{ mb: 3 }}>
          <SectionHeader
            icon={<GroupIcon sx={{ fontSize: 16 }} />}
            title="Reviewers"
          />
          {isTeamRequest(request) && (
            <DSTypography variant="body2" sx={{ mb: 0.75 }}>
              Open to the team — the first approval completes it.{" "}
              {slackThreadUrl(request) && (
                <a
                  href={slackThreadUrl(request) ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  Slack thread
                </a>
              )}
            </DSTypography>
          )}
          {isTeamRequest(request) &&
            checks.map((check) => (
              <DSBox
                key={check.userId}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
              >
                <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                  {userName(check.userId, users)}
                </DSTypography>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  checked by hand · {formatRelativeDate(check.at)}
                </DSTypography>
              </DSBox>
            ))}
          {request.reviewers.map((entry) => {
            const snoozed =
              entry.snoozedUntil && new Date(entry.snoozedUntil) > new Date();
            return (
              <DSBox
                key={entry.userId}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
              >
                <DSBox
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: REVIEWER_STATUS_COLOR[entry.status],
                  }}
                />
                <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                  {userName(entry.userId, users)}
                </DSTypography>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  {REVIEWER_STATUS_LABEL[entry.status]}
                  {entry.respondedAt &&
                    ` · ${formatRelativeDate(entry.respondedAt)}`}
                  {entry.status === "pending" &&
                    entry.remindCount > 0 &&
                    ` · reminded ${entry.remindCount}×`}
                  {snoozed && " · snoozed"}
                  {isChecked(entry.userId) && " · checked by hand"}
                </DSTypography>
                {requester && !closed && (
                  <DSSecondaryButton
                    size="small"
                    disabled={busy !== null}
                    onClick={() =>
                      run("check", () =>
                        isChecked(entry.userId)
                          ? unmarkChecked(request.id, entry.userId)
                          : markChecked(request.id, entry.userId),
                      )
                    }
                    sx={{ borderRadius: 2, ml: "auto" }}
                  >
                    {isChecked(entry.userId) ? "Undo check" : "Mark checked"}
                  </DSSecondaryButton>
                )}
              </DSBox>
            );
          })}
        </DSBox>

        <DSBox>
          <SectionHeader
            icon={<HistoryIcon sx={{ fontSize: 16 }} />}
            title="Timeline"
          />
          {events.map((event, index) => (
            <DSBox
              key={`${event.at}-${index}`}
              sx={{ display: "flex", gap: 1.5, mb: 0.75 }}
            >
              <DSTypography
                variant="caption"
                sx={{ color: "text.secondary", minWidth: 88, flexShrink: 0 }}
              >
                {formatRelativeDate(event.at)}
              </DSTypography>
              <DSTypography variant="caption">
                {describeEvent(event, users)}
              </DSTypography>
            </DSBox>
          ))}
        </DSBox>
      </DSBox>

      <DSBox
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        {error && (
          <DSTypography
            variant="caption"
            sx={{ color: "error.main", width: "100%" }}
          >
            {error}
          </DSTypography>
        )}
        {closed && (
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            {request.outcome ? OUTCOME_LABEL[request.outcome] : "Closed"}
            {request.closedAt && ` · ${formatRelativeDate(request.closedAt)}`}
          </DSTypography>
        )}
        {canCheckSelf && (
          <DSSecondaryButton
            size="small"
            startIcon={<CheckIcon fontSize="small" />}
            disabled={busy !== null}
            onClick={() =>
              run("check", () =>
                isChecked(uid)
                  ? unmarkChecked(request.id)
                  : markChecked(request.id),
              )
            }
            sx={{ borderRadius: 2 }}
          >
            {isChecked(uid) ? "Undo my check" : "I checked it"}
          </DSSecondaryButton>
        )}
        {!closed && requester && (
          <>
            <DSTooltip
              title={
                hasNewCommits(request)
                  ? "New commits were pushed. Ask everyone to look again."
                  : "Start a new round: every reviewer goes back to pending."
              }
            >
              <DSPrimaryButton
                size="small"
                startIcon={<ReplayIcon fontSize="small" />}
                disabled={busy !== null}
                onClick={() => run("re-request", () => reRequest(request.id))}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Re-request review
              </DSPrimaryButton>
            </DSTooltip>
            <DSSecondaryButton
              size="small"
              startIcon={<NotificationsActiveIcon fontSize="small" />}
              disabled={busy !== null || request.status !== "open"}
              onClick={() => run("remind", () => remind(request.id))}
              sx={{ borderRadius: 2 }}
            >
              Remind now
            </DSSecondaryButton>
            <DSSecondaryButton
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              disabled={busy !== null}
              onClick={() => setEditing(true)}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </DSSecondaryButton>
            <DSBox sx={{ flex: 1 }} />
            <DSErrorButton
              size="small"
              startIcon={<LinkOffIcon fontSize="small" />}
              disabled={busy !== null}
              onClick={() => {
                if (window.confirm("Cancel this review request?")) {
                  run("cancel", () => cancel(request.id));
                }
              }}
              sx={{ borderRadius: 2 }}
            >
              Cancel request
            </DSErrorButton>
          </>
        )}
        {!closed && reviewer && !requester && (
          <>
            <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
              Review on GitHub — approvals sync back here automatically.
            </DSTypography>
            <DSBox sx={{ flex: 1 }} />
            <DSSecondaryButton
              size="small"
              startIcon={<SnoozeIcon fontSize="small" />}
              disabled={busy !== null || request.status !== "open"}
              onClick={(event) => setSnoozeAnchor(event.currentTarget)}
              sx={{ borderRadius: 2 }}
            >
              Snooze reminders
            </DSSecondaryButton>
            <DSPopover
              open={!!snoozeAnchor}
              anchorEl={snoozeAnchor}
              onClose={() => setSnoozeAnchor(null)}
            >
              {SNOOZE_OPTIONS_HOURS.map((hours) => (
                <DSMenuItem
                  key={hours}
                  onClick={() => {
                    setSnoozeAnchor(null);
                    run("snooze", () => snooze(request.id, hours));
                  }}
                >
                  {hours} hours
                </DSMenuItem>
              ))}
            </DSPopover>
          </>
        )}
      </DSBox>

      {editing && (
        <ReviewRequestModal
          existing={request}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

export default function ReviewDetailPanel({
  requestId,
  fallback,
  users,
  onClose,
}: ReviewDetailPanelProps) {
  // Actions write the fresh document into the detail cache; read it from
  // there so the drawer never shows a stale status after a click.
  const { request: live, isError, isLoading } = useReviewById(requestId);
  const shown = live ?? fallback;

  return (
    <DetailDrawer
      open={!!requestId}
      onClose={onClose}
      contentKey={requestId ?? undefined}
    >
      {shown ? (
        <DetailContent request={shown} users={users} onClose={onClose} />
      ) : (
        <DSBox sx={{ p: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <DSTypography
            variant="body2"
            sx={{ color: "text.secondary", flex: 1 }}
          >
            {isError
              ? "This review request could not be loaded. It may have been deleted."
              : isLoading
                ? "Loading…"
                : ""}
          </DSTypography>
          <DSIconButton size="small" onClick={onClose}>
            <CloseIcon />
          </DSIconButton>
        </DSBox>
      )}
    </DetailDrawer>
  );
}
